import TextAbbreviationControl from 'modules/Abbreviations/components/ToolbarAbbreviation/TextAbbreviationControl';
import React from 'react';
import { EditorProps, EditorSetters } from 'components/ArtboardAssets/CallToAction/editor';
import { CTAStyles, CTAStylesSetters } from 'components/ArtboardAssets/CallToAction/utils/styles';
import { ToolbarDropdownType } from 'components/EditorToolbar/constants';
import CharStyleControl from 'components/EditorToolbar/controls/CharStyleControl';
import FontFamilyControl from 'components/EditorToolbar/controls/FontFamilyControl';
import ScriptControl from 'components/EditorToolbar/controls/ScriptControl';
import { IconType } from 'components/Icon/constants';
import Subtitle from 'components/Toolbar/BaseToolbar/components/Subtitle';
import { Title, HOVER_HINT } from 'components/Toolbar/constants';
import AssetAlignment from 'components/Toolbar/Controls/AssetAlignment/Group';
import AssetColor from 'components/Toolbar/Controls/AssetColor';
import BackgroundImage from 'components/Toolbar/Controls/BackgroundImage';
import Border from 'components/Toolbar/Controls/Border';
import BorderRadius from 'components/Toolbar/Controls/BorderRadius';
import FillColor from 'components/Toolbar/Controls/FillColor';
import FitToCell from 'components/Toolbar/Controls/FitToCell';
import FontColor from 'components/Toolbar/Controls/FontColor';
import FontSize from 'components/Toolbar/Controls/FontSize';
import Link from 'components/Toolbar/Controls/Link';
import Padding from 'components/Toolbar/Controls/Padding';
import TextHorizontalAlignment from 'components/Toolbar/Controls/TextHorizontalAlignment/Group';
import TextVerticalAlignment from 'components/Toolbar/Controls/TextVerticalAlignment';
import AutoFitContentToggle from 'components/Toolbar/Shared/AutoFitContentToggle';
import ControlGroup from 'components/Toolbar/Shared/ControlGroup';
import HeightWidth from 'components/Toolbar/Shared/HeightWidth';
import {
  AssetAlignmentDirection,
  AssetAlignmentType,
  DefaultCallToActionFontColor,
  MinCellWidth,
  ProjectsConfig,
  ProjectType } from 'const';
import { intlGet } from 'utils/intlGet';
import BaseToolbar from '../BaseToolbar';
import callToActionStyles from './styles.module.scss';

type Props = {
  styles: CTAStyles;
  stylesSetters: CTAStylesSetters;
  projectType: ProjectType;
  cellHeight: number;
  maxCellHeight: number;
  minCellHeight: number;
  toggleCellHeight: (height: number) => number;
  cellWidth: number;
  toggleColumnWidth: (newWidth: number) => number;
  disableCellWidthEditing: boolean;
  link: string;
  toggleLink(link: string): void;
  height: number;
  maxHeight?: number;
  width: number;
  maxWidth: number;
  layoutId: string;
  isIncreasingWidthDisabled: boolean;
  returnFocusToEditor: () => void;
  toggleAutoFitContent(): void;
  isAutoFitContent: boolean;
  editorProps: EditorProps;
  editorSetters: EditorSetters;
};

const Toolbar: React.FunctionComponent<Props> = (props) => {
  const {
    styles,
    stylesSetters,
    cellHeight,
    cellWidth,
    disableCellWidthEditing,
    height,
    isIncreasingWidthDisabled,
    layoutId,
    link,
    maxCellHeight,
    maxHeight,
    maxWidth,
    minCellHeight,
    projectType,
    toggleCellHeight,
    toggleColumnWidth,
    toggleLink,
    width,
    returnFocusToEditor,
    toggleAutoFitContent,
    isAutoFitContent,
    editorProps,
    editorSetters,
  } = props;
  const { allowBackgroundOpacity, allowBackgroundImage } = ProjectsConfig[projectType];
  const [activeDropdown, setActiveDropdown] = React.useState<ToolbarDropdownType | null>(null);

  const toggleTextHorizontalAlignment = (alignment: AssetAlignmentType): void =>
    stylesSetters.textAlignment(styles.textAlignment.set(AssetAlignmentDirection.HORIZONTAL, alignment));

  const toggleTextVerticalAlignment = (alignment: AssetAlignmentType): void =>
    stylesSetters.textAlignment(styles.textAlignment.set(AssetAlignmentDirection.VERTICAL, alignment));

  const toggleFitToCell = (): void => stylesSetters.fitToCell(!styles.fitToCell);

  const wrapperOnMouseDownHackCallback = (event: React.MouseEvent<HTMLDivElement>, dropDownType: ToolbarDropdownType): void => {
    event.preventDefault();
    setActiveDropdown(activeDropdown !== dropDownType ? dropDownType : null);
  };

  const closeDialog = (event: MouseEvent, type, isTarget): void => {
    const isStyleDropdownOpen = activeDropdown === type;

    if (!isTarget && isStyleDropdownOpen) {
      setActiveDropdown(null);
    }
  };

  return (
    <BaseToolbar layoutId={layoutId} dataTestId='callToActionToolbar-baseToolbar'>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'Cell')} dataTestId='callToActionToolbar-subtitle-cell'/>
      <ControlGroup dataTestId='controlGroup0'>
        <HeightWidth
          height={cellHeight}
          maxHeight={maxCellHeight}
          minHeight={minCellHeight}
          toggleHeight={toggleCellHeight}
          disableCellWidthEditing={disableCellWidthEditing}
          minWidth={MinCellWidth.CTA}
          width={cellWidth}
          toggleColumnWidth={toggleColumnWidth}
          isIncreasingWidthDisabled={isIncreasingWidthDisabled}
          toggleAutoFitContent={toggleAutoFitContent}
          isAutoFitContent={isAutoFitContent}
          dataTestId='callToActionToolbar-heightWidth-cell'
        />
        <AutoFitContentToggle
          isAutoFitContent={isAutoFitContent}
          isAutoHeight={false}
          toggleAutoFitContent={toggleAutoFitContent}
          dataTestId='callToActionToolbar-autoFit'
        />
      </ControlGroup>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'CallToAction')} dataTestId='callToActionToolbar-subtitle-callToAction' />
      <ControlGroup dataTestId='controlGroup1'>
        <HeightWidth
          height={height}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          minWidth={MinCellWidth.CTA}
          toggleHeight={stylesSetters.height}
          toggleWidth={stylesSetters.width}
          width={width}
          dataTestId='callToActionToolbar-heightWidth-callToAction'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup2'>
        <FitToCell
          fitToCell={styles.fitToCell}
          toggleFitToCell={toggleFitToCell}
          dataTestId='callToActionToolbar-fitToCell'
        />
        <AssetAlignment
          alignment={styles.alignment}
          toggleAlignment={stylesSetters.alignment}
          dataTestId='callToActionToolbar-assetAlignment'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup3'>
        <FontFamilyControl
          wrapperOnMouseDownHackCallback={wrapperOnMouseDownHackCallback}
          returnFocusToEditor={returnFocusToEditor}
          closeDialog={closeDialog}
          activeDropdown={activeDropdown}
          setFontFamily={editorSetters.fontFamily}
          activeFont={editorProps.fontFamily}
          dataTestId='callToActionToolbar-fontFamilyControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup4'>
        <FontSize
          fontSize={editorProps.fontSize}
          toggleFontSize={editorSetters.fontSize}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='callToActionToolbar-fontSizeControl'
        />
      </ControlGroup>
      <ControlGroup>
        <CharStyleControl
          dataTestId='callToActionToolbar-charStyleControl'
          activeStyles={editorProps.charStyle}
          onToggle={editorSetters.charStyle}
        />
        <ScriptControl type={editorProps.scriptStyle} onToggle={editorSetters.scriptStyle} />
        <FontColor
          color={editorProps.fontColor}
          defaultColor={DefaultCallToActionFontColor}
          toggleFontColor={editorSetters.fontColor}
          dataTestId='callToActionToolbar-fontColorControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup6'>
        <TextHorizontalAlignment
          alignment={styles.textAlignment.get(AssetAlignmentDirection.HORIZONTAL)}
          toggleAlignment={toggleTextHorizontalAlignment}
          dataTestId='callToActionToolbar-textHorizontalAlignment'
        />
        <TextVerticalAlignment
          alignment={styles.textAlignment.get(AssetAlignmentDirection.VERTICAL)}
          toggleAlignment={toggleTextVerticalAlignment}
          dataTestId='callToActionToolbar-textVerticalAlignment'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup7'>
        <Link isDisabled={false} link={link} toggleLink={toggleLink} />
        <TextAbbreviationControl
          selectedText={editorProps.abbreviationTerm}
          abbreviationId={editorProps.abbreviationId}
          onChange={editorSetters.abbreviationId}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='callToActionToolbar-textAbbreviation'
        />
        <Padding
          padding={styles.assetPadding}
          togglePadding={stylesSetters.assetPadding}
          dropdownTitle={Title.PADDING_INTERNAL}
          icon={IconType.PADDING_INTERNAL}
          title={Title.PADDING_INTERNAL}
          dataTestId='callToActionToolbar-paddingInternal'
        />
        <Padding
          padding={styles.padding}
          togglePadding={stylesSetters.padding}
          dropdownTitle={Title.PADDING_EXTERNAL}
          dataTestId='callToActionToolbar-paddingExternal'
        />
        <AssetColor
          activeColor={styles.assetBackgroundColor}
          toggleColor={stylesSetters.assetBackgroundColor}
          opacity={styles.assetBackgroundOpacity}
          toggleOpacity={allowBackgroundOpacity ? stylesSetters.assetBackgroundOpacity : undefined}
          gradient={styles.assetBackgroundGradient}
          toggleGradient={stylesSetters.assetBackgroundGradient}
          dataTestId='callToActionToolbar-assetColor'
        />
        <BorderRadius
          borderRadius={styles.assetBorderRadius}
          title={HOVER_HINT.CALL_TO_ACTION}
          toggleBorderRadius={stylesSetters.assetBorderRadius}
          dataTestId='callToActionToolbar-borderRadius-callToAction'
        />
      </ControlGroup>
      <Border
        className='ml-3'
        border={styles.border}
        toggleBorder={stylesSetters.border}
        dataTestId='callToActionToolbar-border'
      />
      <FillColor
        activeColor={styles.backgroundColor}
        toggleColor={stylesSetters.backgroundColor}
        opacity={styles.backgroundColorOpacity}
        toggleOpacity={allowBackgroundOpacity ? stylesSetters.backgroundColorOpacity : undefined}
        gradient={styles.backgroundGradient}
        toggleGradient={stylesSetters.backgroundGradient}
        dataTestId='callToActionToolbar-fillColor'
      />
      {allowBackgroundImage && <BackgroundImage
        backgroundImage={styles.backgroundImage}
        toggleBackgroundImage={stylesSetters.backgroundImage}
        dataTestId='callToActionToolbar-backgroundImage'
      />}
      <BorderRadius
        borderRadius={styles.borderRadius}
        title={HOVER_HINT.CELL}
        dropdownWarningMessage={intlGet('EditorToolbar.Tooltip', 'WarningMessage')}
        toggleBorderRadius={stylesSetters.borderRadius}
        dropdownClassName={callToActionStyles.borderRadiusDropdown}
        iconTooltipClassName={callToActionStyles.iconTooltip}
        dropdownWarningIconClassName={callToActionStyles.lastDropdownWarningIcon}
        dataTestId='callToActionToolbar-borderRadius-cell'
      />
    </BaseToolbar>
  );
};

export default Toolbar;
